import { defaultAllSelection } from "services/constants/selections";
import { setPayslipTemplateId } from "services/Redux/userManagament";

// Redux/topbar uses "99999" to mean "All Companies"; a payslip template is
// stored per company, so it can only be resolved for a specific company.
export const ALL_COMPANIES_ID = String(defaultAllSelection);

export const isSpecificCompany = (companyId) =>
  companyId !== null &&
  companyId !== undefined &&
  companyId !== "" &&
  String(companyId) !== ALL_COMPANIES_ID;

/**
 * Fetch a company's applied payslip template `code` and push it into Redux
 * (`state.userReducer.payslipTemplateId`), which every payslip page reads to
 * decide which template to render. Fails silently — hydration must never
 * interrupt navigation with a toast.
 *
 * @returns {Promise<string|null>} the template code, or null if unresolved.
 */
export const hydratePayslipTemplate = async ({ domain, companyId, dispatch }) => {
  if (!isSpecificCompany(companyId) || !domain) return null;
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(`${domain}company/${companyId}/`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `token ${token}`,
      },
    });
    if (!response.ok) return null;
    const data = await response.json();
    const code = data?.payslip_template_code;
    if (code) {
      dispatch(setPayslipTemplateId(code));
      return code;
    }
  } catch (error) {
    console.error("[payslipTemplate] hydrate failed:", error);
  }
  return null;
};

/**
 * Resolve a company's applied payslip template code from the company list
 * (`company/` endpoint, kept in `state.tokenReducer.companyDetails`). Each
 * record carries `payslip_template_code` (e.g. "detailedpayslip"), which
 * matches a template id in PayslipTemplates/templates.js.
 *
 * Matches by company id first, then by company name (payslip records carry
 * `company_name`). Returns null when the company or its code is not found.
 */
export const getCompanyPayslipTemplateCode = (
  companyDetails,
  { companyId, companyName } = {},
) => {
  const companies = Array.isArray(companyDetails)
    ? companyDetails
    : (companyDetails?.results ?? []);
  const normalize = (value) =>
    String(value ?? "").trim().toLowerCase();
  const company =
    (isSpecificCompany(companyId) &&
      companies.find(
        (item) => String(item?.id ?? item?.company_id) === String(companyId),
      )) ||
    (companyName &&
      companies.find(
        (item) =>
          normalize(item?.companyname ?? item?.company_name) ===
          normalize(companyName),
      )) ||
    null;
  return company?.payslip_template_code || null;
};

/**
 * The backend builds media URLs (company logo, user image) as `http://...`
 * even though it is served over https. The http URL only answers with a 301
 * redirect that has no CORS headers, and @react-pdf loads images with fetch(),
 * so the browser blocks it (a CORS failure from http pages such as localhost,
 * mixed content from https pages). The logo then silently disappears from
 * every payslip PDF. Upgrade to https for any non-local host.
 */
export const secureImageUrl = (url) => {
  if (typeof url !== "string" || !url.startsWith("http://")) return url;
  const rest = url.slice("http://".length);
  const host = rest.split(/[/:?#]/)[0];
  if (/^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/i.test(host)) return url;
  return `https://${rest}`;
};
